import React, { useId } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Drawer, { type DrawerProps } from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Button, type ButtonProps } from '../Button/Button';
import { SlotProps } from '@mui/material/utils/types';
import { TransitionProps } from '@mui/material/transitions/transition';
import { DrawerOwnerState, DrawerTransitionSlotPropsOverrides } from '@mui/material/Drawer/Drawer';

interface SideDrawerAction {
  label: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  buttonProps?: ButtonProps;
}

type SideDrawerActions =
  | {
      type: 'default';
      primaryAction: SideDrawerAction;
    }
  | {
      type: 'custom';
      content: React.ReactNode;
    };

export type SideDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  content: React.ReactNode;
  width?: number | string;
  variant?: DrawerProps['variant'];
  sx?: DrawerProps['sx'];
  paperSx?: DrawerProps['sx'];
  contentRef?: React.Ref<HTMLDivElement>;
  actions?: SideDrawerActions;
  transitionProps?: SlotProps<
    React.ElementType,
    TransitionProps & DrawerTransitionSlotPropsOverrides,
    DrawerOwnerState
  >;
};

const DEFAULT_DRAWER_WIDTH = 669;
const defaultActionButtonProps: ButtonProps = {
  color: 'primary',
  variant: 'contained',
};

const getActionButtonSx = (sx: ButtonProps['sx']) => [{ width: '100%' }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])];

const getActionButtonProps = ({ buttonProps, disabled, onClick }: SideDrawerAction): ButtonProps => ({
  ...defaultActionButtonProps,
  ...buttonProps,
  disabled: disabled ?? buttonProps?.disabled,
  onClick,
  sx: getActionButtonSx(buttonProps?.sx),
});

const getActionContent = (actions?: SideDrawerActions) => {
  if (!actions) {
    return null;
  }

  if (actions.type === 'custom') {
    return actions.content;
  }

  return <Button {...getActionButtonProps(actions.primaryAction)}>{actions.primaryAction.label}</Button>;
};

const SideDrawer = ({
  isOpen,
  onClose,
  title,
  content,
  actions,
  width = DEFAULT_DRAWER_WIDTH,
  transitionProps,
  variant,
  sx,
  paperSx,
  contentRef,
}: SideDrawerProps) => {
  const titleId = useId();
  const actionContent = getActionContent(actions);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      variant={variant}
      sx={sx}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-labelledby': titleId,
          sx: [
            {
              borderTopLeftRadius: 8,
              borderBottomLeftRadius: 8,
              width: { xs: '100vw', sm: width },
              maxWidth: '100vw',
              height: '100dvh',
              boxSizing: 'border-box',
            },
            ...(Array.isArray(paperSx) ? paperSx : paperSx ? [paperSx] : []),
          ],
        },
        transition: transitionProps,
      }}
    >
      <Box sx={{ p: 2.5, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" mb={1.5}>
          <Typography id={titleId} variant="h4" color="brand">
            {title}
          </Typography>

          <IconButton aria-label="Close drawer" size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box ref={contentRef} sx={{ flex: 1, minHeight: 0, overflow: 'auto', pt: 1 }}>
          {content}
        </Box>

        {actionContent && <Box sx={{ mt: 4 }}>{actionContent}</Box>}
      </Box>
    </Drawer>
  );
};

export default SideDrawer;
